import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

// Store cart error banner state globally to persist across component re-renders
/** @type {null | {errorMessage: string}} */
let globalCartErrorBanner = null;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} cartErrorBanner - The cart error banner element.
 * @property {HTMLElement} cartErrorTitle - The cart error title element.
 * @property {HTMLElement} cartErrorMessage - The cart error message element.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartErrorEvent,
    );
    this.addEventListener("change", this.#handleSubscriptionChange);

    this.#restoreErrorBanner();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.removeEventListener(
      ThemeEvents.cartError,
      this.#handleCartErrorEvent,
    );
    this.removeEventListener("change", this.#handleSubscriptionChange);

    // Clean up cached errors to prevent memory leaks
    if (this.cachedErrors) {
      this.cachedErrors.clear();
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Closes the banner error message.
   */
  onCloseBannerError() {
    const { cartErrorBanner } = this.refs;
    if (cartErrorBanner instanceof HTMLElement) {
      cartErrorBanner.classList.add("hidden");
      globalCartErrorBanner = null;
    }
  }

  /**
   * Restores the error banner after a cart update/re-render.
   */
  #restoreErrorBanner() {
    if (globalCartErrorBanner) {
      const { errorMessage } = globalCartErrorBanner;
      setTimeout(() => {
        this.#displayCartErrorBanner(errorMessage, false);
      }, 0);
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key,
      ),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        // Handle 422 errors before attempting JSON parsing
        if (response.status === 422) {
          return response.text().then((errorText) => {
            resetShimmer(this);

            // Try to parse as JSON first (in case it's a structured error)
            let errorMessage = "Unable to update cart. Please try again.";
            try {
              const errorJson = JSON.parse(errorText);
              if (errorJson.errors) {
                errorMessage = errorJson.errors;
              } else if (errorJson.message || errorJson.description) {
                errorMessage = errorJson.message || errorJson.description;
              }
            } catch (jsonParseError) {
              // Handle non-JSON 422 responses (HTML, plain text, etc.)
              if (errorText.includes("<")) {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = errorText;
                const textContent =
                  tempDiv.textContent || tempDiv.innerText || "";
                if (textContent.trim()) {
                  errorMessage = textContent.trim().substring(0, 200);
                }
              } else if (errorText.trim()) {
                errorMessage = errorText.trim().substring(0, 200);
              }
            }

            // Cache the error for the specific line
            this.cachedErrors = this.cachedErrors || new Map();
            this.cachedErrors.set(line, errorMessage);

            // Re-render the cart section to restore the removed item
            this.#reRenderCartWithError(line, errorMessage);
            return null; // Indicate error was handled
          });
        }

        return response.text();
      })
      .then((responseText) => {
        // Skip processing if we already handled a 422 error above
        if (!responseText) return;

        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // Clear the global error banner on successful cart updates
        // This prevents stale limit errors from reappearing after items are removed
        globalCartErrorBanner = null;

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            source: "cart-items-component",
          }),
        );
      });
  }

  /**
   * Handles subscription checkbox/select changes.
   * @param {Event} event - The event.
   */
  #handleSubscriptionChange = (event) => {
    const target = event.target;
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }

    const wrapper = target.closest(".cart-item-subscription");
    if (!wrapper) return;

    const line = Number(wrapper.dataset.line);
    if (!line) return;

    if (target.classList.contains("cart-item-subscription__checkbox")) {
      const dropdown = wrapper.querySelector(
        ".cart-item-subscription__dropdown",
      );
      const select = wrapper.querySelector(".cart-item-subscription__select");
      const isChecked = target.checked;
      if (dropdown) dropdown.classList.toggle("hidden", !isChecked);

      if (!isChecked) {
        this.#updateSellingPlan({ line, sellingPlanId: null });
        return;
      }

      if (select && !select.value && select.options.length) {
        select.value = select.options[0].value;
      }
      const planId = select?.value;
      if (planId) {
        this.#updateSellingPlan({ line, sellingPlanId: planId });
      }
      return;
    }

    if (target.classList.contains("cart-item-subscription__select")) {
      this.#updateSellingPlan({ line, sellingPlanId: target.value });
    }
  };

  /**
   * Updates the selling plan for a line item.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string | null} config.sellingPlanId - The selling plan id.
   */
  #updateSellingPlan({ line, sellingPlanId }) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      "selling-plan:user-action",
    );

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      selling_plan: sellingPlanId,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            source: "cart-items-component",
          }),
        );
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Re-renders the cart section and then displays the cached error for the specified line.
   * @param {number} line - The line item index.
   * @param {string} errorMessage - The error message to display.
   */
  #reRenderCartWithError = (line, errorMessage) => {
    // Re-render the cart section to restore the removed item
    sectionRenderer
      .renderSection(this.sectionId, { cache: false })
      .then(() => {
        // After re-rendering, display the cached error
        setTimeout(() => {
          this.#displayCachedError(line, errorMessage);
        }, 100); // Small delay to ensure DOM has updated
      })
      .catch((error) => {
        // Fallback if section rendering fails
        console.warn("Failed to re-render cart section:", error);
        this.#displayCachedError(line, errorMessage);
      });
  };

  /**
   * Handles the cart error event from add-to-cart.
   * @param {CartErrorEvent} event - The cart error event.
   */
  #handleCartErrorEvent = (event) => {
    const errorMessage = event.detail.data.message;
    if (!errorMessage) return;

    if (this.#shouldDisplayAsBanner(errorMessage)) {
      this.#displayCartErrorBanner(errorMessage);
    }
  };

  /**
   * Detects if an error should be displayed as a cart-level banner.
   * @param {string} errorMessage - The error message from the cart API.
   * @returns {boolean} True if the error should be displayed as a banner.
   */
  #shouldDisplayAsBanner(errorMessage) {
    const wrapper = /** @type {HTMLElement | null} */ (
      document.querySelector(".cart-items__wrapper")
    );
    if (!wrapper) return false;

    const messageLower = errorMessage.toLowerCase();

    // Check for maximum items error
    const maxItemsKeyword =
      wrapper.dataset.maxItemsKeyword?.toLowerCase() ||
      "maximum number of items";
    if (messageLower.includes(maxItemsKeyword)) {
      return true;
    }

    // Check for maximum quantity per variant error
    const maxQuantityKeyword =
      wrapper.dataset.maxQuantityKeyword?.toLowerCase() || "maximum quantity";
    if (messageLower.includes(maxQuantityKeyword)) {
      return true;
    }

    // Check for general maximum limit errors (e.g., "Cart exceeds maximum limit")
    if (
      messageLower.includes("maximum limit") ||
      messageLower.includes("exceeds maximum")
    ) {
      return true;
    }

    return false;
  }

  /**
   * Displays a cart-level error banner.
   * @param {string} errorMessage - The full error message from the API.
   * @param {boolean} shouldScroll - Whether to scroll to the banner (default: true).
   */
  #displayCartErrorBanner = (errorMessage, shouldScroll = true) => {
    const { cartErrorBanner, cartErrorTitle, cartErrorMessage } = this.refs;

    if (
      cartErrorBanner instanceof HTMLElement &&
      cartErrorTitle instanceof HTMLElement &&
      cartErrorMessage instanceof HTMLElement
    ) {
      // Split error message on first period or newline to separate title from message
      const parts = errorMessage.split(/[.\n]/, 2);
      const title = parts[0]?.trim() || errorMessage;
      const message = parts[1]?.trim() || "";

      cartErrorTitle.textContent = title;
      cartErrorMessage.textContent = message;
      cartErrorBanner.classList.remove("hidden");

      // Store the error globally so it persists across cart section re-renders
      globalCartErrorBanner = { errorMessage };

      if (shouldScroll) {
        const cartDrawer = document.querySelector("cart-drawer-component");
        if (cartDrawer) {
          const scrollContainer =
            cartDrawer.querySelector("[data-scroll-container]") ||
            cartDrawer.querySelector(".cart-drawer__content") ||
            cartDrawer.querySelector(".drawer__content") ||
            cartDrawer;
          if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
          }
        }
      }
    }
  };

  /**
   * Displays a cached error message for a specific line item.
   * @param {number} line - The line item index.
   * @param {string} errorMessage - The error message to display.
   */
  #displayCachedError = (line, errorMessage) => {
    // Check if this is a cart-level error (maximum items or maximum quantity)
    if (this.#shouldDisplayAsBanner(errorMessage)) {
      this.#displayCartErrorBanner(errorMessage);

      // Clean up the cached error
      if (this.cachedErrors) {
        this.cachedErrors.delete(line);
      }
      return;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (
      cartItemError instanceof HTMLElement &&
      cartItemErrorContainer instanceof HTMLElement
    ) {
      cartItemError.textContent = errorMessage;
      cartItemErrorContainer.classList.remove("hidden");

      // Scroll the error into view
      cartItemErrorContainer.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });

      // Clean up the cached error after displaying
      if (this.cachedErrors) {
        this.cachedErrors.delete(line);
      }
    } else {
      console.warn(
        `Error elements not found for line ${line}, error: ${errorMessage}`,
      );
    }
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const errorMessage = parsedResponseText.errors;

    // Check if this is a cart-level error (maximum items or maximum quantity)
    if (this.#shouldDisplayAsBanner(errorMessage)) {
      this.#displayCartErrorBanner(errorMessage);
      return;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => {
          this.#restoreErrorBanner();
        });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      setTimeout(() => {
        this.#restoreErrorBanner();
      }, 0);
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => {
          this.#restoreErrorBanner();
        });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
